import type { CollectionCardConfig, CollectionItem } from '@/lib/blocks/collection/blocks';
import { readRecord, readString } from '@/lib/prop-utils';

export type CollectionButtonStyle = 'primary' | 'secondary';
export type CollectionButtonIconPlacement = 'before' | 'after';

export interface CollectionButtonSettings {
  text: string;
  style: CollectionButtonStyle;
  icon: string | null;
  iconPlacement: CollectionButtonIconPlacement;
}

export const DEFAULT_COLLECTION_BUTTON_SETTINGS: CollectionButtonSettings = {
  text: 'Read More',
  style: 'primary',
  icon: null,
  iconPlacement: 'after',
};

export function readCollectionButtonSettings(
  props: Record<string, unknown> | undefined,
): CollectionButtonSettings {
  const raw = readRecord(props?.buttonSettings);
  if (!raw) return DEFAULT_COLLECTION_BUTTON_SETTINGS;

  const text = readString(raw.text);
  const style = readString(raw.style);
  const icon = readString(raw.icon);
  const iconPlacement = readString(raw.iconPlacement);

  return {
    text: text ?? DEFAULT_COLLECTION_BUTTON_SETTINGS.text,
    style:
      style === 'primary' || style === 'secondary'
        ? style
        : DEFAULT_COLLECTION_BUTTON_SETTINGS.style,
    icon: icon || null,
    iconPlacement:
      iconPlacement === 'before' || iconPlacement === 'after'
        ? iconPlacement
        : DEFAULT_COLLECTION_BUTTON_SETTINGS.iconPlacement,
  };
}

export function applyCollectionButtonSettingsToItems(
  items: CollectionItem[],
  cardConfig: CollectionCardConfig,
  settings: CollectionButtonSettings,
): CollectionItem[] {
  const buttonField = cardConfig.fields.find((field) => field.kind === 'button' && field.enabled);
  if (!buttonField) return items;

  return items.map((item) => {
    const existing = readRecord(item[buttonField.slot]) ?? {};

    return {
      ...item,
      [buttonField.slot]: {
        ...existing,
        text: settings.text,
        style: settings.style,
        ...(settings.icon
          ? { icon: settings.icon, iconPlacement: settings.iconPlacement }
          : { icon: undefined, iconPlacement: undefined }),
      },
    };
  });
}
